import axios from 'axios';
import { clientLogger } from './clientLogger';
import { safeLocalStorage } from '../utils/safeStorage';


// Create the Axios instance
export const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // Always send cookies (refreshToken)
  headers: {
    'Content-Type': 'application/json',
  },
});

let accessToken = null;
let fallbackRefreshToken = safeLocalStorage.getItem('fallback_refresh_token') || null;

/**
 * Sets the access token in memory
 */
export const setAccessToken = (token) => {
  accessToken = token;
};

/**
 * Gets the access token from memory
 */
export const getAccessToken = () => {
  return accessToken;
};

/**
 * Sets the fallback refresh token in memory and localStorage
 */
export const setRefreshTokenFallback = (token) => {
  fallbackRefreshToken = token;
  if (token) {
    safeLocalStorage.setItem('fallback_refresh_token', token);
  } else {
    safeLocalStorage.removeItem('fallback_refresh_token');
  }
};

/**
 * Gets the fallback refresh token
 */
export const getRefreshTokenFallback = () => {
  return fallbackRefreshToken;
};

// Request Interceptor to inject JWT Authorization header
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor to handle 401 Unauthorized errors and refresh token silently
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Check if the error is 401 and we haven't already retried this request
    const isAuthUrl = originalRequest.url?.includes('/auth/login') || 
                      originalRequest.url?.includes('/auth/register') || 
                      originalRequest.url?.includes('/auth/refresh') ||
                      originalRequest.url?.includes('/auth/logout');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthUrl) {
      originalRequest._retry = true;
      
      try {
        console.log('🔄 Access token expired. Attempting silent token refresh...');
        
        const config = { withCredentials: true, headers: {} };
        const fallbackToken = getRefreshTokenFallback();
        if (fallbackToken) {
          config.headers['x-refresh-token'] = fallbackToken;
        }

        // Call the refresh endpoint (reads cookie or headers fallback)
        const response = await axios.post('/api/auth/refresh', { refreshToken: fallbackToken }, config);
        const { accessToken: newToken, refreshToken: newRefreshToken } = response.data;
        
        // Update the tokens
        setAccessToken(newToken);
        if (newRefreshToken) {
          setRefreshTokenFallback(newRefreshToken);
        }
        
        // Update the authorization header for the failed request and retry it
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.warn('❌ Session refresh failed. User must log in again.', refreshError.message);
        setAccessToken(null);
        setRefreshTokenFallback(null);
        
        // Custom event to notify AuthContext to log out the user
        window.dispatchEvent(new Event('auth-session-expired'));
        return Promise.reject(refreshError);
      }
    }
    
    // Log server 5xx and network errors to client telemetry (excluding logging endpoint itself)
    const isLogEndpoint = originalRequest.url?.includes('/logs/client');
    if (!isLogEndpoint) {
      if (!error.response) {
        // Network Error, timeout, CORS
        clientLogger.captureError({
          type: 'NETWORK_ERROR',
          message: `Network Error: ${error.message || 'Failed to fetch'} [${originalRequest.method?.toUpperCase()} ${originalRequest.url}]`,
          metadata: {
            url: originalRequest.url,
            method: originalRequest.method,
            code: error.code
          }
        });
      } else if (error.response.status >= 500) {
        // Server Internal Error
        clientLogger.captureError({
          type: 'NETWORK_ERROR',
          message: `HTTP ${error.response.status}: ${error.response.data?.error || error.message} [${originalRequest.method?.toUpperCase()} ${originalRequest.url}]`,
          metadata: {
            status: error.response.status,
            url: originalRequest.url,
            method: originalRequest.method,
            responseData: error.response.data
          }
        });
      }
    }

    return Promise.reject(error);
  }
);

export const ensureArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.competitions)) return data.competitions;
    if (Array.isArray(data.teams)) return data.teams;
    if (Array.isArray(data.catches)) return data.catches;
    if (Array.isArray(data.reservoirs)) return data.reservoirs;
    if (Array.isArray(data.files)) return data.files;
    if (Array.isArray(data.users)) return data.users;
    if (Array.isArray(data.items)) return data.items;
  }
  return [];
};

// Auth Endpoints
export const authAPI = {
  getAuthConfig: async () => {
    const res = await api.get('/auth/config');
    return res.data;
  },

  getOAuthUrl: async (provider) => {
    const res = await api.get('/auth/oauth-url', { params: { provider } });
    return res.data;
  },

  register: async (email, password, fullName, role) => {
    const res = await api.post('/auth/register', { email, password, fullName, role });
    return res.data;
  },
  
  login: async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { accessToken, refreshToken, user } = res.data;
    setAccessToken(accessToken);
    if (refreshToken) {
      setRefreshTokenFallback(refreshToken);
    }
    return { accessToken, user };
  },
  
  logout: async () => {
    try {
      const config = { headers: {} };
      const fallbackToken = getRefreshTokenFallback();
      if (fallbackToken) {
        config.headers['x-refresh-token'] = fallbackToken;
      }
      await api.post('/auth/logout', { refreshToken: fallbackToken }, config);
    } finally {
      setAccessToken(null);
      setRefreshTokenFallback(null);
    }
  },
  
  refresh: async () => {
    const config = { headers: {} };
    const fallbackToken = getRefreshTokenFallback();
    if (fallbackToken) {
      config.headers['x-refresh-token'] = fallbackToken;
    }
    const res = await api.post('/auth/refresh', { refreshToken: fallbackToken }, config);
    const { accessToken, refreshToken, user } = res.data;
    setAccessToken(accessToken);
    if (refreshToken) {
      setRefreshTokenFallback(refreshToken);
    }
    return { accessToken, user };
  },

  getProfile: async () => {
    const res = await api.get('/auth/profile');
    return res.data;
  },

  updateProfile: async (profileData) => {
    const res = await api.put('/auth/profile', profileData);
    return res.data;
  },

  forgotPassword: async (email) => {
    const res = await api.post('/auth/forgot-password', { email });
    return res.data;
  },

  resetPassword: async (email, code, newPassword) => {
    const res = await api.post('/auth/reset-password', { email, code, newPassword });
    return res.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const res = await api.post('/auth/change-password', { currentPassword, newPassword });
    return res.data;
  },

  grantOrganizerConsent: async () => {
    const res = await api.post('/auth/grant-organizer-consent');
    if (res.data?.accessToken) {
      setAccessToken(res.data.accessToken);
    }
    return res.data;
  },

  getSportsmenSuggestions: async () => {
    const res = await api.get('/auth/sportsmen-suggestions');
    return ensureArray(res.data);
  },

  searchUsers: async (query = '', limit = 20) => {
    const res = await api.get('/auth/users/search', { params: { q: query, limit } });
    return ensureArray(res.data);
  },

  getUsers: async () => {
    const res = await api.get('/auth/users');
    return ensureArray(res.data);
  },

  updateUser: async (id, data) => {
    const res = await api.put(`/auth/users/${id}`, data);
    return res.data;
  },

  blockUser: async (id, reason, type) => {
    const res = await api.post(`/auth/users/${id}/block`, { reason, type });
    return res.data;
  },

  unblockUser: async (id) => {
    const res = await api.post(`/auth/users/${id}/unblock`);
    return res.data;
  },

  resetAllSessions: async (keepCurrentSession = true) => {
    const res = await api.post('/auth/sessions/reset-all', { keepCurrentSession });
    return res.data;
  },

  resetUserSessions: async (id) => {
    const res = await api.post(`/auth/users/${id}/reset-sessions`);
    return res.data;
  },

  getAdminRequests: async () => {
    const res = await api.get('/auth/admin-requests');
    return ensureArray(res.data);
  },

  createAdminRequest: async (data) => {
    const res = await api.post('/auth/admin-requests', data);
    return res.data;
  },

  updateAdminRequest: async (id, data) => {
    const res = await api.put(`/auth/admin-requests/${id}`, data);
    return res.data;
  },

  deleteAdminRequest: async (id) => {
    const res = await api.delete(`/auth/admin-requests/${id}`);
    return res.data;
  }
};

// Storage / File Endpoints
const logClientStorage = (action, details, isError = false) => {
  const timestamp = new Date().toLocaleTimeString();
  const prefix = `📁 [ClientStorageAPI] [${timestamp}] [${action}]`;
  if (isError) {
    console.error(`${prefix} ❌`, details);
  } else {
    console.log(`${prefix} ℹ️`, details);
  }
};

export const storageAPI = {
  getAllFiles: async () => {
    logClientStorage('GET_ALL_FILES_START', {});
    try {
      const res = await api.get('/storage/files');
      const files = ensureArray(res.data);
      logClientStorage('GET_ALL_FILES_SUCCESS', { count: files.length });
      return files;
    } catch (err) {
      logClientStorage('GET_ALL_FILES_ERROR', { error: err.message }, true);
      throw err;
    }
  },
  
  getUploadPresignedUrl: async (name, type) => {
    logClientStorage('GET_UPLOAD_PRESIGNED_URL_START', { name, type });
    try {
      const res = await api.get('/storage/presign-upload', {
        params: { name, type }
      });
      logClientStorage('GET_UPLOAD_PRESIGNED_URL_SUCCESS', { fileId: res.data.fileId, s3Key: res.data.s3Key });
      return res.data; // returns { uploadUrl, fileId, s3Key }
    } catch (err) {
      logClientStorage('GET_UPLOAD_PRESIGNED_URL_ERROR', { name, type, error: err.message }, true);
      throw err;
    }
  },
  
  getDownloadPresignedUrl: async (fileId, options = {}) => {
    logClientStorage('GET_DOWNLOAD_PRESIGNED_URL_START', { fileId, options });
    try {
      const queryParam = options?.forceRefresh ? '?refresh=true' : '';
      const res = await api.get(`/storage/presign-download/${encodeURIComponent(fileId)}${queryParam}`);
      logClientStorage('GET_DOWNLOAD_PRESIGNED_URL_SUCCESS', { fileId, hasDownloadUrl: !!res.data?.downloadUrl });
      return res.data.downloadUrl;
    } catch (err) {
      logClientStorage('GET_DOWNLOAD_PRESIGNED_URL_ERROR', { fileId, error: err.message }, true);
      throw err;
    }
  },
  
  saveFileMeta: async (meta) => {
    logClientStorage('SAVE_FILE_META_START', { meta });
    try {
      const res = await api.post('/storage/meta', meta);
      logClientStorage('SAVE_FILE_META_SUCCESS', { fileId: res.data?.id, uploadStatus: res.data?.uploadStatus });
      return res.data;
    } catch (err) {
      logClientStorage('SAVE_FILE_META_ERROR', { meta, error: err.message }, true);
      throw err;
    }
  },
  
  updateUploadStatus: async (fileId, status) => {
    logClientStorage('UPDATE_UPLOAD_STATUS_START', { fileId, status });
    try {
      const res = await api.put(`/storage/meta/${fileId}/status`, { status });
      logClientStorage('UPDATE_UPLOAD_STATUS_SUCCESS', { fileId, status });
      return res.data;
    } catch (err) {
      logClientStorage('UPDATE_UPLOAD_STATUS_ERROR', { fileId, status, error: err.message }, true);
      throw err;
    }
  },
  
  deleteFile: async (fileId) => {
    logClientStorage('DELETE_FILE_START', { fileId });
    try {
      const res = await api.delete(`/storage/files/${fileId}`);
      logClientStorage('DELETE_FILE_SUCCESS', { fileId, result: res.data });
      return res.data;
    } catch (err) {
      logClientStorage('DELETE_FILE_ERROR', { fileId, error: err.message }, true);
      throw err;
    }
  },

  // Helper method to execute the actual file upload (PUT raw binary) to S3 / Local S3 Mock
  uploadFileBinary: async (uploadUrl, file, onProgress) => {
    // In local simulation, this goes to `/api/storage/local-s3/upload?...`
    // In real S3 production, this goes directly to S3 bucket url.
    // We bypass api instance prefix if it's an absolute url starting with http
    const url = uploadUrl.startsWith('http')
      ? uploadUrl
      : (uploadUrl.startsWith('/api') ? uploadUrl : `/api${uploadUrl}`);
    
    logClientStorage('UPLOAD_FILE_BINARY_START', { fileName: file.name, fileSize: file.size, fileType: file.type, url });

    const startTime = Date.now();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      
      // Explicitly set the content type, bypass any axios configurations or custom headers
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      
      if (onProgress) {
        xhr.upload.onprogress = (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            logClientStorage('UPLOAD_FILE_BINARY_PROGRESS', { fileName: file.name, percentCompleted, loaded: progressEvent.loaded, total: progressEvent.total });
            onProgress(percentCompleted);
          }
        };
      }
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          logClientStorage('UPLOAD_FILE_BINARY_SUCCESS', { fileName: file.name, status: xhr.status, durationMs: Date.now() - startTime });
          resolve({ data: xhr.response, status: xhr.status });
        } else {
          const error = new Error(`Request failed with status code ${xhr.status}`);
          /** @type {any} */ (error).response = {
            status: xhr.status,
            data: xhr.response
          };
          logClientStorage('UPLOAD_FILE_BINARY_FAILED', { fileName: file.name, status: xhr.status, response: xhr.response }, true);
          reject(error);
        }
      };
      
      xhr.onerror = () => {
        logClientStorage('UPLOAD_FILE_BINARY_NETWORK_ERROR', { fileName: file.name, url }, true);
        reject(new Error('Network error during binary upload'));
      };
      
      xhr.send(file);
    });
  }
};

// Flexible base64 S3 upload helper
function dataURLtoBlob(dataurl) {
  if (!dataurl || typeof dataurl !== 'string') {
    return new Blob([], { type: 'application/octet-stream' });
  }
  const parts = dataurl.split(',');
  if (parts.length < 2) {
    try {
      const bstr = atob(dataurl);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: 'application/pdf' });
    } catch (e) {
      return new Blob([], { type: 'application/octet-stream' });
    }
  }
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export const uploadBase64ToS3Flexible = async (fileName, base64Data) => {
  let step = 'Converting base64 data to blob';
  const startTime = Date.now();
  logClientStorage('BASE64_S3_UPLOAD_PIPELINE_START', { fileName, dataLength: base64Data?.length });

  try {
    const blob = dataURLtoBlob(base64Data);
    const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
    logClientStorage('BASE64_CONVERTED_TO_FILE', { fileName, fileSize: file.size, fileType: file.type });
    
    // 1. Get presigned upload URL
    step = 'Acquiring upload pre-signed URL from server';
    const { uploadUrl, fileId, s3Key } = await storageAPI.getUploadPresignedUrl(fileName, blob.type || 'application/pdf');
    
    // 2. Upload binary to S3 / Local mock server
    step = 'Uploading binary stream to S3 / local storage';
    await storageAPI.uploadFileBinary(uploadUrl, file);
    
    // 3. Save metadata
    step = 'Saving file metadata records to database';
    await storageAPI.saveFileMeta({
      id: fileId,
      originalName: fileName,
      mimeType: file.type || 'application/pdf',
      size: file.size,
      s3Key: s3Key
    });
    
    // 4. Update upload status to completed
    step = 'Confirming upload completion with server';
    await storageAPI.updateUploadStatus(fileId, 'completed');
    
    // 5. Retrieve signed download URL
    step = 'Requesting pre-signed download URL';
    const downloadUrl = await storageAPI.getDownloadPresignedUrl(fileId);

    logClientStorage('BASE64_S3_UPLOAD_PIPELINE_SUCCESS', { fileName, fileId, downloadUrl, totalDurationMs: Date.now() - startTime });
    return { downloadUrl, fileId };
  } catch (err) {
    logClientStorage('BASE64_S3_UPLOAD_PIPELINE_ERROR', { fileName, step, error: err.message }, true);
    console.error(`Failed to upload file to live S3 (Error during: ${step}):`, err);
    if (err.response) {
      console.error('Server response info:', err.response.status, err.response.data);
    }
    throw err;
  }
};

export const uploadBlobToS3Flexible = async (fileName, blob) => {
  let step = 'Preparing binary file for upload';
  const startTime = Date.now();
  logClientStorage('BLOB_S3_UPLOAD_PIPELINE_START', { fileName, blobSize: blob.size, blobType: blob.type });

  try {
    const mimeType = blob.type || 'image/jpeg';
    const file = new File([blob], fileName, { type: mimeType });
    logClientStorage('BLOB_CONVERTED_TO_FILE', { fileName, fileSize: file.size, fileType: file.type });
    
    // 1. Get presigned upload URL
    step = 'Acquiring upload pre-signed URL from server';
    const { uploadUrl, fileId, s3Key } = await storageAPI.getUploadPresignedUrl(fileName, mimeType);
    
    // 2. Upload binary to S3 / Local mock server
    step = 'Uploading binary stream to S3 / local storage';
    await storageAPI.uploadFileBinary(uploadUrl, file);
    
    // 3. Save metadata
    step = 'Saving file metadata records to database';
    await storageAPI.saveFileMeta({
      id: fileId,
      originalName: fileName,
      mimeType: mimeType,
      size: file.size,
      s3Key: s3Key
    });
    
    // 4. Update upload status to completed
    step = 'Confirming upload completion with server';
    await storageAPI.updateUploadStatus(fileId, 'completed');
    
    // 5. Retrieve signed download URL
    step = 'Requesting pre-signed download URL';
    const downloadUrl = await storageAPI.getDownloadPresignedUrl(fileId);

    logClientStorage('BLOB_S3_UPLOAD_PIPELINE_SUCCESS', { fileName, fileId, downloadUrl, totalDurationMs: Date.now() - startTime });
    return { downloadUrl, fileId };
  } catch (error) {
    logClientStorage('BLOB_S3_UPLOAD_PIPELINE_FAILURE', { 
      failedStep: step, 
      fileName, 
      errorMessage: error.message,
      errorResponse: error.response?.data
    });
    console.error(`Upload error during stage: ${step}`, error);
    throw error;
  }
};

/**
 * Process competition files array, converting any raw base64 data URIs to file storage uploads.
 */
export const processCompetitionFiles = async (files) => {
  if (!files || !Array.isArray(files) || files.length === 0) return files || [];
  logClientStorage('PROCESS_COMPETITION_FILES_START', { totalFiles: files.length });

  return Promise.all(
    files.map(async (file, index) => {
      if (file && file.url && typeof file.url === 'string' && file.url.startsWith('data:')) {
        try {
          const base64Str = file.url;
          const sizeInBytes = Math.round((base64Str.length - (base64Str.indexOf(',') + 1)) * 0.75);
          const sizeStr = sizeInBytes > 1024 * 1024
            ? `${(sizeInBytes / (1024 * 1024)).toFixed(1)} МБ`
            : `${(sizeInBytes / 1024).toFixed(0)} КБ`;

          logClientStorage('PROCESSING_BASE64_FILE_ITEM', { index, fileName: file.name, estimatedSizeBytes: sizeInBytes });

          const { downloadUrl, fileId } = await uploadBase64ToS3Flexible(file.name || 'protocol.pdf', base64Str);
          return {
            ...file,
            id: fileId,
            url: downloadUrl,
            size: file.size && !file.size.includes('data:') ? file.size : sizeStr
          };
        } catch (err) {
          logClientStorage('PROCESS_COMPETITION_FILE_ITEM_ERROR', { index, fileName: file.name, error: err.message }, true);
          console.error('Failed to upload protocol file to storage:', err);
          return file;
        }
      }
      return file;
    })
  );
};

/**
 * Resolves a fresh presigned download URL for a file object if its URL is an expired S3 presigned URL or if file ID is known.
 */
export const resolveFreshDownloadUrl = async (fileObj, options = {}) => {
  if (!fileObj) return '';
  const file = typeof fileObj === 'string' ? { url: fileObj } : fileObj;
  if (!file.url) return '';
  if (file.url === 'removed_due_to_quota_limits' || file.url.startsWith('data:') || file.url.startsWith('blob:')) {
    return file.url;
  }

  let fileId = file.id;

  let extractedUuid = null;
  let extractedS3Key = null;
  if (typeof file.url === 'string') {
    const viewMatch = file.url.match(/\/api\/storage\/(?:view|files)\/([^\s\?#]+)/i);
    if (viewMatch) {
      extractedUuid = viewMatch[1];
    }
    const uuidMatch = file.url.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (uuidMatch && !extractedUuid) {
      extractedUuid = uuidMatch[1];
    }
    const s3KeyMatch = file.url.match(/public\/uploads\/[^\s\?#]+/i);
    if (s3KeyMatch) {
      extractedS3Key = decodeURIComponent(s3KeyMatch[0]);
    }
  }

  // If file.id is missing or numeric (like competition_files DB ID "1117"), prefer extracted UUID or S3 Key
  if (extractedUuid && (!fileId || /^\d+$/.test(String(fileId)))) {
    fileId = extractedUuid;
  } else if (extractedS3Key && (!fileId || /^\d+$/.test(String(fileId)))) {
    fileId = extractedS3Key;
  } else if (!fileId && extractedUuid) {
    fileId = extractedUuid;
  }

  if (fileId) {
    try {
      logClientStorage('RESOLVE_FRESH_DOWNLOAD_URL_ATTEMPT', { fileId, currentUrl: file.url, options });
      const freshUrl = await storageAPI.getDownloadPresignedUrl(fileId, options);
      if (freshUrl) {
        logClientStorage('RESOLVE_FRESH_DOWNLOAD_URL_SUCCESS', { fileId, freshUrl });
        return freshUrl;
      }
    } catch (err) {
      logClientStorage('RESOLVE_FRESH_DOWNLOAD_URL_WARN', { fileId, error: err.message }, true);
      console.warn('Failed to refresh download URL for file ID:', fileId, err);
    }
  }

  return file.url;
};

// Reservoirs Endpoints (Fallback placeholders or endpoints for next stages)
export const reservoirsAPI = {
  getAll: async (includeDeleted = false) => {
    const res = await api.get('/reservoirs', { params: { includeDeleted } });
    return ensureArray(res.data);
  },
  getDeleted: async () => {
    const res = await api.get('/reservoirs/trash');
    return ensureArray(res.data);
  },
  getDeletionPreview: async (id) => {
    const res = await api.get(`/reservoirs/${id}/deletion-preview`);
    return res.data;
  },
  getById: async (id) => {
    const res = await api.get(`/reservoirs/${id}`);
    return res.data;
  },
  create: async (data) => {
    const res = await api.post('/reservoirs', data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await api.put(`/reservoirs/${id}`, data);
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/reservoirs/${id}`);
    return res.data;
  },
  restore: async (id) => {
    const res = await api.post(`/reservoirs/${id}/restore`);
    return res.data;
  },
  purge: async (id) => {
    const res = await api.delete(`/reservoirs/${id}/purge`);
    return res.data;
  },
  deleteAction: async (id, confirmed = false) => {
    const res = await api.post(`/reservoirs/${id}/delete-action`, { confirmed });
    return res.data;
  }
};

// Sectors Endpoints
export const sectorsAPI = {
  getByReservoirId: async (reservoirId) => {
    const res = await api.get(`/reservoirs/${reservoirId}/sectors`);
    return ensureArray(res.data);
  },
  getById: async (id) => {
    const res = await api.get(`/sectors/${id}`);
    return res.data;
  },
  create: async (reservoirId, data) => {
    const res = await api.post(`/reservoirs/${reservoirId}/sectors`, data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await api.put(`/sectors/${id}`, data);
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/sectors/${id}`);
    return res.data;
  }
};

// Broadcast Endpoints
export const broadcastAPI = {
  getEvents: async (competitionId) => {
    const res = await api.get(`/competitions/${competitionId}/broadcast-events`);
    return ensureArray(res.data);
  },
  createEvent: async (competitionId, eventData) => {
    const res = await api.post(`/competitions/${competitionId}/broadcast-events`, eventData);
    return res.data;
  },
  updateEvent: async (eventId, eventData, competitionId = null) => {
    const url = competitionId ? `/competitions/${competitionId}/broadcast-events/${eventId}` : `/broadcast/${eventId}`;
    const res = await api.put(url, eventData);
    return res.data;
  },
  reactToEvent: async (eventId, emoji) => {
    const res = await api.post(`/broadcast/${eventId}/react`, { emoji });
    return res.data;
  },
  deleteEvent: async (eventId, competitionId = null) => {
    const url = competitionId ? `/competitions/${competitionId}/broadcast-events/${eventId}` : `/broadcast/${eventId}`;
    const res = await api.delete(url);
    return res.data;
  }
};

// Competitions Endpoints
export const competitionsAPI = {
  getAll: async () => {
    const res = await api.get('/competitions');
    return ensureArray(res.data);
  },
  getMy: async () => {
    const res = await api.get('/competitions/my');
    return ensureArray(res.data);
  },
  getById: async (id) => {
    const res = await api.get(`/competitions/${id}`);
    return res.data;
  },
  getLeaderboard: async (id, basis = 'total_weight') => {
    const res = await api.get(`/competitions/${id}/leaderboard`, { params: { basis } });
    return res.data;
  },
  recalculateLeaderboard: async (id) => {
    const res = await api.post(`/competitions/${id}/leaderboard/recalculate`);
    return res.data;
  },
  getBroadcastEvents: async (competitionId) => {
    return broadcastAPI.getEvents(competitionId);
  },
  create: async (data) => {
    const res = await api.post('/competitions', data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await api.put(`/competitions/${id}`, data);
    return res.data;
  },
  patch: async (id, partialData) => {
    const res = await api.patch(`/competitions/${id}`, partialData);
    return res.data;
  },
  finalizeStats: async (id, options = {}) => {
    const res = await api.post(`/competitions/${id}/finalize-stats`, options);
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/competitions/${id}`);
    return res.data;
  },
  registerTeam: async (competitionId, data) => {
    const res = await api.post(`/competitions/${competitionId}/teams`, data);
    return res.data;
  },
  updateTeam: async (competitionId, teamId, data) => {
    const res = await api.patch(`/competitions/${competitionId}/teams/${teamId}`, data);
    return res.data;
  },
  deleteTeam: async (competitionId, teamId) => {
    const res = await api.delete(`/competitions/${competitionId}/teams/${teamId}`);
    return res.data;
  },
  syncTeams: async (competitionId, teams) => {
    const res = await api.put(`/competitions/${competitionId}/teams/sync`, { teams });
    return res.data;
  },
  // Competition Files Endpoints - independent from competition object edits
  saveDrawPdfWorkspaceBackup: async (filename, pdfBase64) => {
    try {
      const res = await api.post('/save-draw-pdf', { filename, pdfBase64 });
      return res.data;
    } catch (fsErr) {
      console.warn('Backend save-draw-pdf workspace note:', fsErr);
      return null;
    }
  },
  getFiles: async (competitionId) => {
    const res = await api.get(`/competitions/${competitionId}/files`);
    return res.data;
  },
  addFile: async (competitionId, fileData) => {
    const res = await api.post(`/competitions/${competitionId}/files`, fileData);
    return res.data;
  },
  deleteFile: async (competitionId, fileId) => {
    const res = await api.delete(`/competitions/${competitionId}/files/${fileId}`);
    return res.data;
  },
  getWeighingSessions: async (competitionId) => {
    const res = await api.get(`/competitions/${competitionId}/weighing-sessions`);
    return ensureArray(res.data);
  },
  createWeighingSession: async (competitionId, data) => {
    const res = await api.post(`/competitions/${competitionId}/weighing-sessions`, data);
    return res.data;
  },
  updateWeighingSession: async (competitionId, sessionId, data) => {
    const res = await api.patch(`/competitions/${competitionId}/weighing-sessions/${sessionId}`, data);
    return res.data;
  },
  deleteWeighingSession: async (competitionId, sessionId) => {
    const res = await api.delete(`/competitions/${competitionId}/weighing-sessions/${sessionId}`);
    return res.data;
  },
  startWeighingSession: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/weighing-sessions/start`, data);
    return res.data;
  },
  endWeighingSession: async (competitionId, sessionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/weighing-sessions/${sessionId}/end`, data);
    return res.data;
  },
  generateWeighingProtocol: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/generate-weighing-protocol`, data);
    return res.data;
  },
  generateDrawProtocol: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/generate-draw-protocol`, data);
    return res.data;
  },
  generateZonalProtocol: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/generate-zonal-protocol`, data);
    return res.data;
  },
  generateTeamProtocol: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/generate-team-protocol`, data);
    return res.data;
  },
  generatePersonalProtocol: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/generate-personal-protocol`, data);
    return res.data;
  }
};

// Weighing Sessions Dedicated API
export const weighingSessionsAPI = {
  getSessions: async (competitionId) => {
    const res = await api.get(`/competitions/${competitionId}/weighing-sessions`);
    return ensureArray(res.data);
  },
  createSession: async (competitionId, data) => {
    const res = await api.post(`/competitions/${competitionId}/weighing-sessions`, data);
    return res.data;
  },
  updateSession: async (competitionId, sessionId, data) => {
    const res = await api.patch(`/competitions/${competitionId}/weighing-sessions/${sessionId}`, data);
    return res.data;
  },
  deleteSession: async (competitionId, sessionId) => {
    const res = await api.delete(`/competitions/${competitionId}/weighing-sessions/${sessionId}`);
    return res.data;
  },
  startSession: async (competitionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/weighing-sessions/start`, data);
    return res.data;
  },
  endSession: async (competitionId, sessionId, data = {}) => {
    const res = await api.post(`/competitions/${competitionId}/weighing-sessions/${sessionId}/end`, data);
    return res.data;
  }
};

// Competition Teams Endpoints
export const competitionTeamsAPI = {
  getTeams: async (competitionId) => {
    const res = await api.get(`/competitions/${competitionId}/teams`);
    return ensureArray(res.data);
  },
  registerTeam: async (competitionId, data) => {
    const res = await api.post(`/competitions/${competitionId}/teams`, data);
    return res.data;
  },
  updateTeam: async (competitionId, teamId, data) => {
    const res = await api.patch(`/competitions/${competitionId}/teams/${teamId}`, data);
    return res.data;
  },
  deleteTeam: async (competitionId, teamId) => {
    const res = await api.delete(`/competitions/${competitionId}/teams/${teamId}`);
    return res.data;
  },
  syncTeams: async (competitionId, teams) => {
    const res = await api.put(`/competitions/${competitionId}/teams/sync`, { teams });
    return res.data;
  },
  bulkUpdate: async (competitionId, updates) => {
    const res = await api.patch(`/competitions/${competitionId}/teams/batch`, { updates });
    return res.data;
  },
  getAdmissionStatus: async (competitionId) => {
    const res = await api.get(`/competitions/${competitionId}/admission-status`);
    return res.data;
  }
};

// Teams Endpoints
export const teamsAPI = {
  getAll: async () => {
    const res = await api.get('/teams');
    return ensureArray(res.data);
  },
  getById: async (id) => {
    const res = await api.get(`/teams/${id}`);
    return res.data;
  },
  create: async (data) => {
    const res = await api.post('/teams', data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await api.put(`/teams/${id}`, data);
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/teams/${id}`);
    return res.data;
  }
};

// Catches Endpoints
export const catchesAPI = {
  getAll: async () => {
    const res = await api.get('/catches');
    return ensureArray(res.data);
  },
  getByCompetition: async (compId) => {
    const res = await api.get(`/catches/competition/${compId}`);
    return ensureArray(res.data);
  },
  getById: async (id) => {
    const res = await api.get(`/catches/${id}`);
    return res.data;
  },
  create: async (data) => {
    const res = await api.post('/catches', data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await api.put(`/catches/${id}`, data);
    return res.data;
  },
  verify: async (id, isVerified = true) => {
    const res = await api.put(`/catches/${id}/verify`, { isVerified });
    return res.data;
  },
  verifySession: async ({ competitionId, sessionId, catchIds, isVerified = true }) => {
    const res = await api.post('/catches/verify-session', {
      competitionId,
      sessionId,
      catchIds,
      isVerified
    });
    return res.data;
  },
  delete: async (id) => {
    const res = await api.delete(`/catches/${id}`);
    return res.data;
  }
};

// AI & Neural Network Services API
export const aiAPI = {
  getStatus: async () => {
    const res = await api.get('/ai/status');
    return res.data;
  },
  getSettings: async () => {
    const res = await api.get('/ai/settings');
    return res.data;
  },
  updateSettings: async (settings) => {
    const res = await api.put('/ai/settings', settings);
    return res.data;
  },
  addModel: async (type, modelName) => {
    const res = await api.post('/ai/settings/models', { type, modelName });
    return res.data;
  },
  removeModel: async (type, modelName) => {
    const res = await api.delete('/ai/settings/models', { data: { type, modelName } });
    return res.data;
  },
  generateText: async (params) => {
    const res = await api.post('/ai/generate-text', params);
    return res.data;
  },
  generateImage: async (params) => {
    const res = await api.post('/ai/generate-image', params);
    return res.data;
  }
};

// System Settings Dedicated API (Protocols, Generators, App Options)
export const settingsAPI = {
  getProtocolSettings: async () => {
    const res = await api.get('/settings/protocols');
    return res.data;
  },
  updateProtocolSettings: async (settings) => {
    const res = await api.put('/settings/protocols', settings);
    return res.data;
  }
};

// Client Error Logs API (Admin telemetry)

export const logsAPI = {
  getClientLogs: async (params = {}) => {
    const res = await api.get('/logs/client', { params });
    return res.data;
  },
  clearClientLogs: async () => {
    const res = await api.delete('/logs/client');
    return res.data;
  }
};



export default api;



